// Centralised, reusable formatting of API outcomes into human-readable alert
// messages. Kept pure (no React) so it can be unit-tested and reused anywhere.
//
// The api.js layer throws Error objects shaped as { message, status, data },
// where `message` is already sourced from the server's `data.error` /
// `data.message`. These helpers add a safety net so a call site never surfaces
// an empty string, '[object Object]', or a raw stack trace to the user.

const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';

// Status codes that already carry a clear, user-facing message from api.js —
// we pass those through verbatim rather than overriding them.
const FRIENDLY_STATUS_MESSAGES = Object.freeze({
  401: 'Your session has expired. Please log in again.',
  403: 'You do not have permission to perform this action.',
  413: 'That file is too large to upload.',
  429: 'Too many requests. Please wait a moment and try again.',
});

// Coerce whatever a caller threw into a trimmed, non-empty string, or '' when
// there's nothing usable. Handles Error instances, plain strings, and the
// { error } / { message } shapes a raw response body can take.
const toMessageString = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (value instanceof Error) return (value.message || '').trim();
  if (typeof value === 'object') {
    const candidate = value.error || value.message;
    return typeof candidate === 'string' ? candidate.trim() : '';
  }
  return '';
};

/**
 * getApiErrorMessage — resolve a clean, user-facing message from an API error.
 *
 * Priority:
 *   1. A friendly override for known status codes (401/403/…)
 *   2. The error's own message (already extracted from data.error/data.message)
 *   3. A message nested on error.data ({ error } | { message })
 *   4. The provided fallback, else the generic default.
 *
 * Never returns '', '[object Object]', or a stringified object.
 */
export const getApiErrorMessage = (error, fallback = DEFAULT_ERROR_MESSAGE) => {
  const status = error?.status;
  if (status && FRIENDLY_STATUS_MESSAGES[status]) {
    // Prefer a server-provided message when present (it's usually more specific
    // than the generic status text), otherwise use the friendly default.
    return toMessageString(error) || FRIENDLY_STATUS_MESSAGES[status];
  }

  const direct = toMessageString(error);
  if (direct) return direct;

  const fromData = toMessageString(error?.data);
  if (fromData) return fromData;

  return fallback || DEFAULT_ERROR_MESSAGE;
};

/**
 * getApiSuccessMessage — resolve a success message. Prefers an explicit message,
 * falls back to a server-provided data.message when the caller opted into a
 * success alert without supplying copy. Returns '' when there's nothing to show
 * (callers should treat '' as "don't show a success alert").
 */
export const getApiSuccessMessage = (data, explicitMessage = '') => {
  const explicit = toMessageString(explicitMessage);
  if (explicit) return explicit;
  return toMessageString(data);
};
